import React, { useMemo, useState } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ReviewForm from './ReviewForm';

const Stars = ({ value, size = 'sm' }) => (
  <span className={`m-stars m-stars-${size}`} aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={value >= n - 0.25 ? 'is-on' : ''} aria-hidden="true">★</span>
    ))}
  </span>
);

// Ratings summary, filters (photos / verified / stars), review list with
// photo thumbnails + lightbox, and the "Write a review" button.
const ReviewsSection = ({ productId, productName, reviews = [] }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [writing, setWriting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images, index }

  const stats = useMemo(() => {
    const total = reviews.length;
    const dist = [5, 4, 3, 2, 1].map((n) => ({ n, count: reviews.filter((r) => Math.round(r.rating) === n).length }));
    const avg = total ? reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / total : 0;
    return {
      total,
      avg,
      dist,
      withPhotos: reviews.filter((r) => r.images?.length).length,
      verified: reviews.filter((r) => r.verifiedPurchase).length,
      allPhotos: reviews.flatMap((r) => r.images || [])
    };
  }, [reviews]);

  const shown = reviews.filter((r) => {
    if (filter === 'photos') return r.images?.length;
    if (filter === 'verified') return r.verifiedPurchase;
    if (/^[1-5]$/.test(filter)) return Math.round(r.rating) === Number(filter);
    return true;
  });

  const openWrite = () => {
    if (!user) {
      navigate('/auth', { state: { from: window.location.pathname + window.location.search } });
      return;
    }
    setWriting(true);
  };

  return (
    <section className="m-section m-reviews" id="reviews">
      <div className="m-section-head">
        <div>
          <h2 className="m-section-title">Ratings &amp; reviews</h2>
          {stats.total > 0 && <p className="m-section-note">{stats.total} review{stats.total === 1 ? '' : 's'}</p>}
        </div>
        <button type="button" className="m-btn light" onClick={openWrite}>Write a review</button>
      </div>

      {stats.total === 0 ? (
        <div className="m-empty">No reviews yet. Bought this? Be the first to share how it fits and feels.</div>
      ) : (
        <>
          <div className="m-review-summary">
            <div className="m-review-score">
              <strong>{stats.avg.toFixed(1)}</strong>
              <Stars value={stats.avg} size="lg" />
              <span>{stats.total} rating{stats.total === 1 ? '' : 's'}{stats.verified ? ` · ${stats.verified} verified` : ''}</span>
            </div>
            <div className="m-review-bars">
              {stats.dist.map(({ n, count }) => (
                <button type="button" key={n} className="m-review-bar" onClick={() => setFilter(filter === String(n) ? 'all' : String(n))} aria-pressed={filter === String(n)}>
                  <span>{n}★</span>
                  <span className="m-bar"><span style={{ width: `${stats.total ? (count / stats.total) * 100 : 0}%` }} /></span>
                  <span>{count}</span>
                </button>
              ))}
            </div>
            {stats.allPhotos.length > 0 && (
              <div className="m-review-gallery">
                <span>Customer photos</span>
                <div>
                  {stats.allPhotos.slice(0, 6).map((src, i) => (
                    <button type="button" key={src + i} onClick={() => setLightbox({ images: stats.allPhotos, index: i })} aria-label={`Open customer photo ${i + 1}`}>
                      <img src={mediaUrl(src)} alt="" loading="lazy" />
                      {i === 5 && stats.allPhotos.length > 6 && <em>+{stats.allPhotos.length - 6}</em>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="m-review-filters" role="group" aria-label="Filter reviews">
            {[
              ['all', `All (${stats.total})`],
              ['photos', `With photos (${stats.withPhotos})`],
              ['verified', `Verified buyers (${stats.verified})`]
            ].map(([key, label]) => (
              <button type="button" key={key} className={filter === key ? 'is-active' : ''} onClick={() => setFilter(key)} aria-pressed={filter === key}>
                {label}
              </button>
            ))}
            {/^[1-5]$/.test(filter) && (
              <button type="button" className="is-active" onClick={() => setFilter('all')}>{filter}★ only ×</button>
            )}
          </div>

          <ul className="m-review-list">
            {shown.map((r) => (
              <li key={r._id} className="m-review">
                <div className="m-review-head">
                  <Stars value={r.rating} />
                  {r.title && <strong>{r.title}</strong>}
                </div>
                <p className="m-review-text">{r.comment}</p>
                {r.images?.length > 0 && (
                  <div className="m-review-photos">
                    {r.images.map((src, i) => (
                      <button type="button" key={src} onClick={() => setLightbox({ images: r.images, index: i })} aria-label={`Open photo ${i + 1} from ${r.customerName}`}>
                        <img src={mediaUrl(src)} alt="" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="m-review-meta">
                  <span>{r.customerName}</span>
                  {r.verifiedPurchase && <span className="m-verified">Verified buyer</span>}
                  <span>{new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                {r.adminReply && (
                  <div className="m-review-reply">
                    <strong>Reply from Mithralaya</strong>
                    <p>{r.adminReply}</p>
                  </div>
                )}
              </li>
            ))}
            {shown.length === 0 && <li className="m-empty">No reviews match this filter.</li>}
          </ul>
        </>
      )}

      {writing && (
        <ReviewForm productId={productId} productName={productName} onClose={() => setWriting(false)} />
      )}

      {lightbox && (
        <div className="m-modal-backdrop m-lightbox" onClick={() => setLightbox(null)}>
          <div className="m-lightbox-inner" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Customer photo">
            <button type="button" className="m-modal-close" onClick={() => setLightbox(null)} aria-label="Close">×</button>
            <img src={mediaUrl(lightbox.images[lightbox.index])} alt="Customer photo" />
            {lightbox.images.length > 1 && (
              <div className="m-lightbox-nav">
                <button type="button" onClick={() => setLightbox((l) => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }))} aria-label="Previous photo">‹</button>
                <span>{lightbox.index + 1} / {lightbox.images.length}</span>
                <button type="button" onClick={() => setLightbox((l) => ({ ...l, index: (l.index + 1) % l.images.length }))} aria-label="Next photo">›</button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviewsSection;
